import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Paths where ads must NOT appear (authenticated / admin areas)
const PORTAL_PREFIXES = [
  "/portal",
  "/agents/overview",
  "/agents/workforce",
  "/agents/approvals",
  "/agents/activity",
  "/agents/delegation",
  "/aegis-x/devices",
  "/aegis-x/threats",
  "/aegis-x/incidents",
  "/aegis-x/predator",
  "/projects",
  "/rms",
  "/admin",
  "/auth",
];

const ADSENSE_SRC =
  "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1452747228778250";
const SCRIPT_ID = "google-adsense-script";

function isPortalPage(pathname) {
  return PORTAL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
  );
}

// Read marketing consent from localStorage
function hasMarketingConsent() {
  try {
    const c = JSON.parse(localStorage.getItem("cookieConsent") || "null");
    return c?.marketing === true;
  } catch {
    return false;
  }
}

export default function GoogleAdSense() {
  const { pathname } = useLocation();
  const pushed = useRef(false);
  const isPublic = !isPortalPage(pathname);

  // Always load the AdSense script on public pages so Google's reviewer
  // can verify the site. Consent Mode v2 (set in index.html) controls
  // whether Google serves personalised or non-personalised ads.
  useEffect(() => {
    if (!isPublic) {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
      pushed.current = false;
      return;
    }
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = ADSENSE_SRC;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }
  }, [isPublic]);

  // Push ad units once per page view.
  // We push regardless of marketing consent — Consent Mode v2 already
  // told Google's servers whether to personalise or not. Calling push({})
  // without consent only serves non-personalised (cookieless) ads.
  useEffect(() => {
    if (!isPublic || pushed.current) return;
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (_) {}
  }, [isPublic, pathname]);

  // Reset on every navigation so new page loads get fresh ad calls
  useEffect(() => {
    pushed.current = false;
  }, [pathname]);

  // Script is loaded via useEffect for Google's site review.
  // Ad slots are not rendered here to avoid injecting blank iframes
  // below the footer. Placements live inside individual page bodies.
  return null;
}
